import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Truck, Plus, Pencil, Archive, ArchiveRestore, Search, Wrench, Package, X, Trash2, ClipboardList, FileText, FileSpreadsheet, MapPin, AlertTriangle, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { inv, INV_API } from '../../lib/invApi';
import RestockDialog from '../../components/inventory/RestockDialog';

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

const InventoryLoadouts = () => {
  const [templates, setTemplates] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [applyTpl, setApplyTpl] = useState(null);

  const load = useCallback(async () => {
    const { data } = await inv.get('/loadout-templates', { include_inactive: showArchived });
    setTemplates(data);
  }, [showArchived]);

  const loadLookups = useCallback(async () => {
    const [it, d, l] = await Promise.all([
      inv.get('/items'),
      inv.get('/departments', { type: 'trade' }),
      inv.get('/locations'),
    ]);
    setItems(it.data); setDepartments(d.data); setLocations(l.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (t) => {
    const { data } = await inv.get(`/loadout-templates/${t.id}`);
    setEditing(data); setEditorOpen(true);
  };
  const archive = async (t) => {
    if (!window.confirm(`Archive loadout "${t.name}"?`)) return;
    try { await inv.patch(`/loadout-templates/${t.id}/archive`); toast.success('Archived'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const unarchive = async (t) => { try { await inv.patch(`/loadout-templates/${t.id}/unarchive`); toast.success('Restored'); load(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="inv-loadouts-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><Truck className="w-7 h-7 text-[#D4AF37]" /> Van Loadouts</h2>
          <p className="text-[#A3A3A3]">Standard / target van stock lists. Apply to a van to compare the target with Estimated On Van (a rough reference) — stock is never changed automatically. Company purchasing uses the Reorder List.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer mr-1">
            <input data-testid="inv-loadouts-show-archived" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#D4AF37]" />
            Show archived
          </label>
          <Button data-testid="inv-loadout-new-btn" onClick={openNew} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Loadout</Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div data-testid="inv-loadouts-empty" className="bg-[#141414] border border-[#262626] rounded-md p-10 text-center text-[#525252]">
          No loadout templates yet. Create your first standard van template to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} data-testid={`inv-loadout-card-${t.id}`} className={`bg-[#141414] border rounded-md p-4 flex flex-col ${t.active === false ? 'border-amber-800/40 opacity-70' : 'border-[#262626]'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-white font-semibold flex items-center gap-2 flex-wrap">{t.name}
                    {t.active === false && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px]">ARCHIVED</Badge>}
                    {t.department && <Badge className="bg-[#0A0A0A] text-[#A3A3A3] border-[#262626] text-[10px]">{t.department}</Badge>}
                  </h3>
                  {t.description && <p className="text-[#A3A3A3] text-xs mt-1">{t.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm text-[#A3A3A3]">
                <span className="flex items-center gap-1.5"><Package className="w-4 h-4 text-[#D4AF37]" /> {t.material_count} materials</span>
                <span className="flex items-center gap-1.5"><Wrench className="w-4 h-4 text-[#D4AF37]" /> {t.tool_count} tools</span>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#262626]">
                {t.active === false ? (
                  <Button data-testid={`inv-loadout-unarchive-${t.id}`} onClick={() => unarchive(t)} size="sm" variant="outline" className="border-green-700/40 text-green-400 hover:bg-green-700/10"><ArchiveRestore className="w-4 h-4 mr-1" /> Restore</Button>
                ) : (
                  <>
                    <Button data-testid={`inv-loadout-apply-${t.id}`} onClick={() => setApplyTpl(t)} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><ClipboardList className="w-4 h-4 mr-1" /> Apply to Van</Button>
                    <Button data-testid={`inv-loadout-edit-${t.id}`} onClick={() => openEdit(t)} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Pencil className="w-4 h-4" /></Button>
                    <Button data-testid={`inv-loadout-archive-${t.id}`} onClick={() => archive(t)} size="sm" variant="outline" className="border-[#262626] text-amber-400 hover:bg-amber-900/20"><Archive className="w-4 h-4" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <LoadoutEditor open={editorOpen} onOpenChange={setEditorOpen} editing={editing} items={items} departments={departments} onSaved={() => { setEditorOpen(false); load(); }} />
      <ApplyLoadoutDialog template={applyTpl} onOpenChange={(o) => !o && setApplyTpl(null)} locations={locations} />
    </div>
  );
};

// ── Template editor (fully editable materials + separate tools) ──────────────
const LoadoutEditor = ({ open, onOpenChange, editing, items, departments, onSaved }) => {
  const blank = { name: '', description: '', department: '', materials: [], tools: [] };
  const [form, setForm] = useState(blank);
  const [matSearch, setMatSearch] = useState('');
  const activeDepts = departments.filter((d) => d.active !== false).map((d) => d.name);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || '',
        description: editing.description || '',
        department: editing.department || '',
        materials: (editing.materials || []).map((m) => ({ item_id: m.item_id, name: m.name, sku: m.sku, unit: m.unit, preferred_supplier: m.preferred_supplier, target_qty: m.target_qty })),
        tools: (editing.tools || []).map((t) => ({ ...t })),
      });
    } else { setForm(blank); }
    setMatSearch('');
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosenIds = useMemo(() => new Set(form.materials.map((m) => m.item_id)), [form.materials]);
  const matResults = useMemo(() => {
    if (!matSearch.trim()) return [];
    const q = matSearch.toLowerCase();
    return items.filter((it) => !chosenIds.has(it.id) && `${it.name} ${it.sku || ''} ${it.barcode || ''}`.toLowerCase().includes(q)).slice(0, 8);
  }, [matSearch, items, chosenIds]);

  const addMaterial = (it) => {
    setForm((f) => ({ ...f, materials: [...f.materials, { item_id: it.id, name: it.name, sku: it.sku, unit: it.unit, preferred_supplier: it.preferred_supplier, target_qty: 1 }] }));
    setMatSearch('');
  };
  const setMatQty = (id, v) => setForm((f) => ({ ...f, materials: f.materials.map((m) => m.item_id === id ? { ...m, target_qty: v } : m) }));
  const removeMat = (id) => setForm((f) => ({ ...f, materials: f.materials.filter((m) => m.item_id !== id) }));

  const addTool = () => setForm((f) => ({ ...f, tools: [...f.tools, { name: '', qty: 1, notes: '' }] }));
  const setTool = (i, key, v) => setForm((f) => ({ ...f, tools: f.tools.map((t, idx) => idx === i ? { ...t, [key]: v } : t) }));
  const removeTool = (i) => setForm((f) => ({ ...f, tools: f.tools.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = {
      name: form.name.trim(),
      description: form.description,
      department: form.department || null,
      materials: form.materials.map((m) => ({ item_id: m.item_id, target_qty: parseFloat(m.target_qty) || 0 })),
      tools: form.tools.filter((t) => (t.name || '').trim()).map((t) => ({ name: t.name.trim(), qty: parseFloat(t.qty) || 1, notes: t.notes || '' })),
    };
    try {
      if (editing) await inv.put(`/loadout-templates/${editing.id}`, payload);
      else await inv.post('/loadout-templates', payload);
      toast.success(editing ? 'Loadout updated' : 'Loadout created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-loadout-editor" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit — ${editing.name}` : 'New Van Loadout'}</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">Materials come from Master Inventory. Tools are a separate checklist and are not tracked as stock.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name *</Label><Input data-testid="inv-loadout-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Standard Service Van" className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div>
              <Label className="text-xs">Department</Label>
              <select data-testid="inv-loadout-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3">
                <option value="">Unassigned</option>
                {activeDepts.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Description</Label><Textarea data-testid="inv-loadout-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          </div>

          {/* Materials */}
          <div className="border border-[#262626] rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2 flex items-center gap-2"><Package className="w-4 h-4 text-[#D4AF37]" /> Materials ({form.materials.length})</p>
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="inv-loadout-material-search" value={matSearch} onChange={(e) => setMatSearch(e.target.value)} placeholder="Search Master Inventory to add a material…" className="pl-10 bg-[#0A0A0A] border-[#262626] text-white" />
              {matResults.length > 0 && (
                <div data-testid="inv-loadout-material-results" className="absolute z-20 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#262626] rounded-md max-h-56 overflow-y-auto shadow-xl">
                  {matResults.map((it) => (
                    <button key={it.id} data-testid={`inv-loadout-material-opt-${it.id}`} onClick={() => addMaterial(it)} className="w-full text-left px-3 py-2 hover:bg-[#1a1a1a] border-b border-[#262626] last:border-0">
                      <span className="text-white text-sm">{it.name}</span>
                      <span className="text-[#525252] text-xs ml-2">{it.sku || 'no SKU'} · {it.preferred_supplier || 'Unassigned'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {form.materials.length === 0 ? <p className="text-[#525252] text-xs">No materials yet — search above to add.</p> : (
              <div className="space-y-1.5">
                {form.materials.map((m) => (
                  <div key={m.item_id} data-testid={`inv-loadout-material-row-${m.item_id}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{m.name}</p>
                      <p className="text-[#525252] text-xs truncate">{m.sku || 'no SKU'} · {m.preferred_supplier || 'Unassigned'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Label className="text-[10px] text-[#A3A3A3]">Target</Label>
                      <Input data-testid={`inv-loadout-material-qty-${m.item_id}`} type="number" step="any" min="0" value={m.target_qty} onChange={(e) => setMatQty(m.item_id, e.target.value)} className="w-20 h-8 bg-[#141414] border-[#262626] text-white text-sm" />
                      <span className="text-[#525252] text-xs w-8">{m.unit}</span>
                    </div>
                    <Button data-testid={`inv-loadout-material-remove-${m.item_id}`} onClick={() => removeMat(m.item_id)} size="sm" variant="ghost" className="h-8 px-2 text-red-400 hover:bg-red-900/20"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tools */}
          <div className="border border-[#262626] rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold flex items-center gap-2"><Wrench className="w-4 h-4 text-[#D4AF37]" /> Tools ({form.tools.length})</p>
              <Button data-testid="inv-loadout-tool-add" onClick={addTool} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"><Plus className="w-4 h-4 mr-1" /> Add tool</Button>
            </div>
            {form.tools.length === 0 ? <p className="text-[#525252] text-xs">No tools yet — tools are a checklist, not tracked stock.</p> : (
              <div className="space-y-1.5">
                {form.tools.map((t, i) => (
                  <div key={i} data-testid={`inv-loadout-tool-row-${i}`} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
                    <Input data-testid={`inv-loadout-tool-name-${i}`} value={t.name} onChange={(e) => setTool(i, 'name', e.target.value)} placeholder="Tool name" className="flex-1 h-8 bg-[#141414] border-[#262626] text-white text-sm" />
                    <Input data-testid={`inv-loadout-tool-qty-${i}`} type="number" step="any" min="0" value={t.qty} onChange={(e) => setTool(i, 'qty', e.target.value)} className="w-16 h-8 bg-[#141414] border-[#262626] text-white text-sm" />
                    <Input data-testid={`inv-loadout-tool-notes-${i}`} value={t.notes} onChange={(e) => setTool(i, 'notes', e.target.value)} placeholder="Notes" className="flex-1 h-8 bg-[#141414] border-[#262626] text-white text-sm" />
                    <Button data-testid={`inv-loadout-tool-remove-${i}`} onClick={() => removeTool(i)} size="sm" variant="ghost" className="h-8 px-2 text-red-400 hover:bg-red-900/20"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626]">Cancel</Button>
          <Button data-testid="inv-loadout-save" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{editing ? 'Save' : 'Create'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Apply to van: shortage/build list + set targets + supplier export ────────
const ApplyLoadoutDialog = ({ template, onOpenChange, locations }) => {
  const open = !!template;
  const [locId, setLocId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restockOpen, setRestockOpen] = useState(false);
  const vanLocs = locations.filter((l) => l.archived !== true);

  useEffect(() => { if (open) { setLocId(''); setResult(null); } }, [open, template]);

  const run = useCallback(async (lid) => {
    if (!template || !lid) return;
    setLoading(true);
    try { const { data } = await inv.get(`/loadout-templates/${template.id}/apply`, { location_id: lid }); setResult(data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to build list'); }
    setLoading(false);
  }, [template]);

  const onPickLoc = (v) => { setLocId(v); run(v); };

  const setTargets = async () => {
    if (!window.confirm('Set the template target/par quantities on this location? This does NOT change actual stock.')) return;
    try {
      const { data } = await inv.post(`/loadout-templates/${template.id}/set-targets`, { location_id: locId });
      toast.success(`${data.targets_set} targets set — stock unchanged`);
      run(locId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const exportUrl = (fmt) => `${INV_API}/loadout-templates/${template.id}/export?location_id=${locId}&format=${fmt}`;
  const download = (fmt) => { window.open(exportUrl(fmt), '_blank'); };

  // Group shortage rows by supplier for display.
  const grouped = useMemo(() => {
    if (!result) return [];
    const g = {};
    result.materials.forEach((m) => { (g[m.preferred_supplier || 'Supplier Not Assigned'] = g[m.preferred_supplier || 'Supplier Not Assigned'] || []).push(m); });
    return Object.keys(g).sort().map((sup) => ({ supplier: sup, rows: g[sup] }));
  }, [result]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-loadout-apply-dialog" className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#D4AF37]" /> Apply "{template?.name}" to a Van</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">A loadout is the standard / target van stock list. Estimated On Van comes from transfers in and may not reflect what is physically on the van, so the Difference is a rough guide — not a guaranteed restock or purchase quantity. Applying never changes stock.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          <select data-testid="inv-loadout-apply-location" value={locId} onChange={(e) => onPickLoc(e.target.value)} className="h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-3 min-w-[220px]">
            <option value="">Select a van / location…</option>
            {vanLocs.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
          </select>
          {locId && <Button data-testid="inv-loadout-set-targets" onClick={setTargets} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"><Target className="w-4 h-4 mr-1" /> Set as Targets</Button>}
        </div>

        {loading && <p className="text-[#A3A3A3] py-6 text-center">Building list…</p>}

        {result && !loading && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Materials" value={result.totals.material_lines} />
              <Stat label="Lines below target (est.)" value={result.totals.short_lines} />
              <Stat label="Difference value (rough)" value={`~${money(result.totals.total_shortage_value)}`} />
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button data-testid="inv-loadout-restock" onClick={() => setRestockOpen(true)} size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><ClipboardList className="w-4 h-4 mr-1" /> Prepare Shop → Van Transfer</Button>
              <Button data-testid="inv-loadout-export-pdf" onClick={() => download('pdf')} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] hover:text-white"><FileText className="w-4 h-4 mr-1" /> Export PDF</Button>
              <Button data-testid="inv-loadout-export-csv" onClick={() => download('csv')} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] hover:text-white"><FileSpreadsheet className="w-4 h-4 mr-1" /> Export CSV</Button>
              <span className="text-[10px] text-[#525252] self-center">Difference is a guide only — company purchasing uses the Reorder List.</span>
            </div>

            <div data-testid="inv-loadout-shortage-list" className="border border-[#262626] rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                  <tr>
                    <th className="text-left px-3 py-2 text-[#A3A3A3]">Item</th>
                    <th className="text-right px-3 py-2 text-[#A3A3A3]">Target</th>
                    <th className="text-right px-3 py-2 text-[#A3A3A3]" data-testid="inv-loadout-current-col">{result.location?.type === 'van' ? 'Estimated On Van' : 'On Hand'}</th>
                    <th className="text-right px-3 py-2 text-[#A3A3A3]" data-testid="inv-loadout-diff-col">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <React.Fragment key={g.supplier}>
                      <tr className="bg-[#0A0A0A]/50"><td colSpan="4" className="px-3 py-1.5 text-[#D4AF37] text-xs font-bold uppercase tracking-wide">{g.supplier}</td></tr>
                      {g.rows.map((m) => (
                        <tr key={m.item_id} data-testid={`inv-loadout-short-row-${m.item_id}`} className="border-b border-[#262626] last:border-0">
                          <td className="px-3 py-2 text-white">{m.name}<span className="text-[#525252] text-xs ml-2">{m.sku || ''}</span></td>
                          <td className="px-3 py-2 text-right text-[#A3A3A3]">{m.target_qty}</td>
                          <td className="px-3 py-2 text-right text-[#A3A3A3] italic">{result.location?.type === 'van' ? '~' : ''}{m.current_qty}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${m.shortage > 0 ? 'text-amber-300' : 'text-green-400'}`}>{m.shortage > 0 ? `${m.shortage} below target` : '✓'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {result.tools?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-[#D4AF37]" /> Tool checklist</p>
                <div className="flex flex-wrap gap-2">
                  {result.tools.map((t, i) => <Badge key={i} className="bg-[#0A0A0A] text-[#A3A3A3] border-[#262626]">{t.name}{t.qty > 1 ? ` ×${t.qty}` : ''}{t.notes ? ` · ${t.notes}` : ''}</Badge>)}
                </div>
              </div>
            )}
          </div>
        )}
        {result && (
          <RestockDialog
            open={restockOpen}
            onOpenChange={setRestockOpen}
            locations={locations}
            toLocationId={result.location.id}
            toLocationName={result.location.name}
            templateId={template.id}
            templateName={template.name}
            onDone={() => run(locId)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</p>
    <p className={`text-lg font-bold ${accent ? 'text-red-400' : 'text-white'}`}>{value}</p>
  </div>
);

export default InventoryLoadouts;
